// RigSentinel — Phase 1 Auth/Storage-Scoping.
//
// Single source of truth for "who is signed in on this device right now?"
// The real credential is always the HttpOnly session cookie (server-side).
// This module only caches a MINIMAL, non-sensitive marker
// ({ uid, email }) so that:
//   1. Account-scoped storage (lib/accountScope.js) has a UID to key off of
//      synchronously, without waiting on a network round trip on every render.
//   2. The app can stay usable (offline grace) if the device goes offline
//      after a real, verified sign-in — never as a substitute for real auth.
//
// verifyAuth() always calls GET /api/auth/me. A 401 clears the marker and
// signs the app out locally; a network failure (offline) falls back to the
// last-known marker; a 200 refreshes it.

import { useEffect, useState } from "react";

const MARKER_KEY = "rsc.authState.v1"; // intentionally UNSCOPED — this IS the scope seed
const API_BASE = process.env.REACT_APP_BACKEND_URL;

function _loadMarker() {
  try {
    const raw = localStorage.getItem(MARKER_KEY);
    if (!raw) return null;
    const m = JSON.parse(raw);
    return m && m.uid ? m : null;
  } catch {
    return null;
  }
}

// Seed synchronously at module load — before any component renders — so
// scoped storage reads/writes work immediately on a warm reload instead of
// blocking behind the first /api/auth/me round trip.
const _seed = _loadMarker();
let _uid = _seed ? _seed.uid : null;
let _email = _seed ? _seed.email : null;

const _listeners = new Set();
function _emit() { _listeners.forEach((l) => { try { l(); } catch { /* noop */ } }); }

export function subscribeAuthState(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

export function getCurrentUid() { return _uid; }
export function getCurrentEmail() { return _email; }

function _setAuth(uid, email) {
  _uid = uid;
  _email = email;
  try {
    localStorage.setItem(MARKER_KEY, JSON.stringify({ uid, email, verifiedAt: new Date().toISOString() }));
  } catch { /* storage unavailable — in-memory state still updated */ }
  _emit();
}

function _clearAuth() {
  _uid = null;
  _email = null;
  try { localStorage.removeItem(MARKER_KEY); } catch { /* noop */ }
  _emit();
}

/** Explicit sign-out. Called by the header's "Sign out" action. */
export function signOutLocally() { _clearAuth(); }

/**
 * Verify identity against the server.
 *   - 200            → confirms/refreshes the marker, returns { uid, email, offline:false }
 *   - 401            → definitely signed out, clears the marker, returns null
 *   - network error  → OFFLINE GRACE: if a marker already exists, keep trusting
 *                       it and return { uid, email, offline:true }; otherwise null.
 */
export async function verifyAuth() {
  if (!API_BASE) return _uid ? { uid: _uid, email: _email, offline: true } : null;
  try {
    const res = await fetch(`${API_BASE}/api/auth/me`, { credentials: "include" });
    if (res.status === 401) { _clearAuth(); return null; }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const uid = String(data.userId || "");
    const email = String(data.email || "");
    if (!uid) { _clearAuth(); return null; }
    _setAuth(uid, email);
    return { uid, email, offline: false };
  } catch {
    // Network unreachable — do NOT treat as signed out. Trust the last
    // verified marker (if any) so the app stays usable offline.
    if (_uid) return { uid: _uid, email: _email, offline: true };
    return null;
  }
}

/**
 * React hook for gating components (RequireAuth) and header UI.
 * `loading` is true only until the first verifyAuth() resolves. If a device
 * marker was already present, `uid` is non-null from the very first render
 * (optimistic — reconciled moments later by the network check).
 */
export function useAuthState() {
  const [state, setState] = useState(() => ({ uid: _uid, email: _email, loading: true, offline: false }));

  useEffect(() => {
    let cancelled = false;
    verifyAuth().then((res) => {
      if (cancelled) return;
      setState({ uid: res ? res.uid : null, email: res ? res.email : null, loading: false, offline: !!(res && res.offline) });
    });
    const unsub = subscribeAuthState(() => {
      setState((s) => ({ ...s, uid: _uid, email: _email }));
    });
    return () => { cancelled = true; unsub(); };
  }, []);

  return state;
}

export const __TEST__ = { MARKER_KEY };
